<template>
  <div :class="props.bgColor" class="relative h-5 overflow-clip rounded-full" data-cy="expBar">
    <div class="bg-tm-petrol-base relative h-full" :style="{ width: `${animatedFill}%` }">
      <div class="absolute right-2 flex h-full flex-row items-center gap-1">
        <span data-cy="expBarText" class="text-right text-sm text-white">{{
          displayedPoints
        }}</span>
        <GenericIcon icon="icon-[mingcute--flash-fill]" icon-size="size-3" class="text-white" />
      </div>
    </div>

    <div
      v-if="isGoalVisible && currentLevel"
      class="absolute top-0 right-2 z-10 flex h-full flex-row items-center gap-1"
    >
      <span class="text-tm-petrol-base text-right text-sm">{{ currentLevel.maxPoints + 1 }}</span>
      <GenericIcon
        icon="icon-[mingcute--flag-4-fill]"
        icon-size="size-3"
        class="text-tm-petrol-base"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { ActivityPointLevel, ActivityPointTrackingFullSizeDto } from '@therame/shared';
import { GenericIcon } from '@therame/ui';

export interface ExperienceBarProps {
  hasLeveledUp?: boolean;
  currentPoints?: number;
  currentLevel?: ActivityPointLevel;
  previousLevel?: ActivityPointLevel;
  bgColor?: string;
  latestTracking?: ActivityPointTrackingFullSizeDto;
}

const props = withDefaults(defineProps<ExperienceBarProps>(), {
  hasLeveledUp: undefined,
  currentPoints: undefined,
  currentLevel: undefined,
  previousLevel: undefined,
  bgColor: undefined,
  latestTracking: undefined,
});

const ANIMATION_DURATION_MS = 1300;

/** Füllstand (0–100) eines Punktestands innerhalb eines Levels (Default: aktuelles Level). */
const pctForPoints = (points: number, level = props.currentLevel): number => {
  if (!level) return 0;
  const { minPoints, maxPoints } = level;
  const range = maxPoints - minPoints;
  if (range <= 0) return 0;
  return Math.min(100, Math.max(0, ((points - minPoints) / range) * 100));
};

const targetPoints = computed(() => props.currentPoints ?? 0);
const targetFill = computed(() => pctForPoints(targetPoints.value));

const startPoints = (): number =>
  Math.max(0, targetPoints.value - (props.latestTracking?.points ?? 0));

/** Füllstand vor dem Zuwachs – bei Levelaufstieg relativ zum vorherigen Level. */
const startFill = (): number =>
  props.hasLeveledUp && props.previousLevel
    ? pctForPoints(startPoints(), props.previousLevel)
    : pctForPoints(startPoints());

const displayedPoints = ref<number>(Math.round(startPoints()));
const animatedFill = ref<number>(startFill());

const isGoalVisible = computed(() => targetFill.value <= 85);

let rafId: number | undefined;
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);

interface TweenOptions {
  fromFill: number;
  toFill: number;
  fromPoints: number;
  toPoints: number;
  onComplete?: () => void;
}

const tween = ({ fromFill, toFill, fromPoints, toPoints, onComplete }: TweenOptions) => {
  displayedPoints.value = fromPoints;
  animatedFill.value = fromFill;

  if (typeof requestAnimationFrame === 'undefined') {
    displayedPoints.value = toPoints;
    animatedFill.value = toFill;
    onComplete?.();
    return;
  }

  const begin = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - begin) / ANIMATION_DURATION_MS);
    const eased = easeOut(t);
    displayedPoints.value = Math.round(fromPoints + (toPoints - fromPoints) * eased);
    animatedFill.value = fromFill + (toFill - fromFill) * eased;
    if (t < 1) {
      rafId = requestAnimationFrame(step);
    } else {
      onComplete?.();
    }
  };
  rafId = requestAnimationFrame(step);
};

const runAnimation = () => {
  if (rafId !== undefined) cancelAnimationFrame(rafId);

  const fromPoints = Math.round(startPoints());
  const toPoints = Math.round(targetPoints.value);

  // Levelaufstieg: erst das alte Level bis 100% füllen, dann ins neue Level zurücksetzen.
  if (props.hasLeveledUp && props.previousLevel) {
    const newLevelMinPoints = props.currentLevel?.minPoints ?? toPoints;
    tween({
      fromFill: pctForPoints(fromPoints, props.previousLevel),
      toFill: 100,
      fromPoints,
      toPoints: props.previousLevel.maxPoints,
      onComplete: () =>
        tween({
          fromFill: 0,
          toFill: targetFill.value,
          fromPoints: newLevelMinPoints,
          toPoints,
        }),
    });
    return;
  }

  // Normalfall: einphasig vom Start- zum Zielwert.
  if (fromPoints === toPoints && startFill() === targetFill.value) {
    displayedPoints.value = toPoints;
    animatedFill.value = targetFill.value;
    return;
  }
  tween({ fromFill: startFill(), toFill: targetFill.value, fromPoints, toPoints });
};

onMounted(runAnimation);
// Das Tracking kommt teils erst nach dem Mount (Query) – dann Animation nachholen.
watch(() => props.latestTracking, runAnimation);

onUnmounted(() => {
  if (rafId !== undefined) cancelAnimationFrame(rafId);
});
</script>
